// src/tour/TourContext.js
//
// The tour engine's brain: holds the active tour's state, exposes actions,
// and persists everything to Supabase (tour_progress + tour_events).
//
// State machine is intentionally small and reusable — adding a new tour means
// adding a script (see src/tour/scripts/), never touching this file.
//
// Persistence discipline (Design Principle #19 — Silent-Write Guard):
// every .update() chains .select('id') and checks data.length. A zero-row
// update is upgraded to an insert rather than silently doing nothing.

import { createContext, useReducer, useCallback, useMemo, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import { getScript } from './scripts'
import { emitTourEvent, onTourEvent, TOUR_EVENT_TYPES } from '../lib/appEvents'

export const TourContext = createContext(null)

const nowIso = () => new Date().toISOString()

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const initialState = {
  activeTourId: null,
  activeScript: null,
  currentStepIndex: 0,
  visitedStepIds: [],
  isPaused: false,            // true while a tour is running (overlay shown)
  homeContext: { homeId: null },
  branch: null,               // { branchId, returnStepIndex } | null
}

function reducer(state, action) {
  switch (action.type) {
    case 'START': {
      const { script, tourId, homeId } = action
      const firstStepId = script?.steps?.[0]?.id
      return {
        ...initialState,
        activeTourId: tourId,
        activeScript: script,
        currentStepIndex: 0,
        visitedStepIds: firstStepId ? [firstStepId] : [],
        isPaused: true,
        homeContext: { homeId: homeId ?? null },
      }
    }
    case 'GOTO': {
      const idx = action.index
      const stepId = state.activeScript?.steps?.[idx]?.id
      const visited = stepId && !state.visitedStepIds.includes(stepId)
        ? [...state.visitedStepIds, stepId]
        : state.visitedStepIds
      return { ...state, currentStepIndex: idx, visitedStepIds: visited }
    }
    case 'ENTER_BRANCH':
      return {
        ...state,
        branch: { branchId: action.branchId, returnStepIndex: state.currentStepIndex },
      }
    case 'EXIT_BRANCH':
      return { ...state, branch: null }
    case 'END':
      return { ...initialState }
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

// Insert-or-update a tour_progress row. home_id is nullable; because Postgres
// treats NULLs as distinct in UNIQUE constraints we match home_id explicitly
// (.is for null, .eq otherwise) so a global tour doesn't spawn duplicate rows.
async function persistProgress(userId, tourId, homeId, patch) {
  if (!userId) return null
  let q = supabase
    .from('tour_progress')
    .update({ ...patch, updated_at: nowIso() })
    .eq('user_id', userId)
    .eq('tour_id', tourId)
  q = homeId == null ? q.is('home_id', null) : q.eq('home_id', homeId)

  const { data, error } = await q.select('id')
  if (error) {
    console.warn('[tour] tour_progress update failed:', error.message)
    return null
  }
  // Silent-write guard: 0 rows means no existing row — insert one.
  if (data && data.length > 0) return data[0]

  const { data: ins, error: insErr } = await supabase
    .from('tour_progress')
    .insert({ user_id: userId, tour_id: tourId, home_id: homeId ?? null, ...patch })
    .select('id')
  if (insErr) {
    console.warn('[tour] tour_progress insert failed:', insErr.message)
    return null
  }
  return ins && ins[0]
}

async function logEvent(userId, tourId, homeId, eventType, { stepId = null, eventData = null } = {}) {
  if (!userId) return
  const { error } = await supabase.from('tour_events').insert({
    user_id: userId,
    tour_id: tourId,
    home_id: homeId ?? null,
    step_id: stepId,
    event_type: eventType,
    event_data: eventData,
  })
  if (error) console.warn('[tour] tour_events insert failed:', error.message)
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function TourProvider({ session, children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  // Freshest state through a ref to avoid stale closures inside async actions.
  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])

  // userId from session prop, kept in a ref so callbacks don't go stale.
  const userIdRef = useRef(session?.user?.id ?? null)
  useEffect(() => { userIdRef.current = session?.user?.id ?? null }, [session])

  const steps = state.activeScript?.steps ?? []
  const currentStep = steps[state.currentStepIndex] ?? null

  const completeTourInternal = useCallback(async () => {
    const { activeTourId, homeContext } = stateRef.current
    const uid = userIdRef.current
    await persistProgress(uid, activeTourId, homeContext.homeId, {
      completed_at: nowIso(),
      abandoned_mid_step: false,
    })
    await logEvent(uid, activeTourId, homeContext.homeId, 'tour_completed', {
      stepId: stateRef.current.activeScript?.steps?.[stateRef.current.currentStepIndex]?.id,
    })
    dispatch({ type: 'END' })
  }, [])

  const goToIndex = useCallback(async (nextIndex, { completedStepId, eventType } = {}) => {
    const { activeTourId, homeContext } = stateRef.current
    const uid = userIdRef.current
    const scriptSteps = stateRef.current.activeScript?.steps ?? []
    if (completedStepId) {
      await logEvent(uid, activeTourId, homeContext.homeId, eventType || 'step_completed', { stepId: completedStepId })
    }
    if (nextIndex >= scriptSteps.length) {
      await completeTourInternal()
      return
    }
    dispatch({ type: 'GOTO', index: nextIndex })
    const nextId = scriptSteps[nextIndex]?.id
    await persistProgress(uid, activeTourId, homeContext.homeId, { last_step_id: nextId })
    await logEvent(uid, activeTourId, homeContext.homeId, 'step_entered', { stepId: nextId })
  }, [completeTourInternal])

  const startTour = useCallback(async (tourId, homeId = null) => {
    const script = getScript(tourId)
    if (!script) {
      console.warn(`[tour] no script registered for "${tourId}"`)
      return
    }
    dispatch({ type: 'START', script, tourId, homeId })
    const uid = userIdRef.current
    await persistProgress(uid, tourId, homeId, {
      started_at: nowIso(),
      last_step_id: script.steps?.[0]?.id ?? null,
      completed_at: null,
      dismissed_at: null,
      abandoned_mid_step: false,
    })
    await logEvent(uid, tourId, homeId, 'tour_started', { stepId: script.steps?.[0]?.id })
    await logEvent(uid, tourId, homeId, 'step_entered', { stepId: script.steps?.[0]?.id })
  }, [])

  const nextStep = useCallback(async () => {
    const s = stateRef.current
    const curId = s.activeScript?.steps?.[s.currentStepIndex]?.id
    await goToIndex(s.currentStepIndex + 1, { completedStepId: curId })
  }, [goToIndex])

  const previousStep = useCallback(async () => {
    const s = stateRef.current
    if (s.currentStepIndex <= 0) return
    dispatch({ type: 'GOTO', index: s.currentStepIndex - 1 })
    const prevId = s.activeScript?.steps?.[s.currentStepIndex - 1]?.id
    await persistProgress(userIdRef.current, s.activeTourId, s.homeContext.homeId, { last_step_id: prevId })
  }, [])

  const skipStep = useCallback(async () => {
    const s = stateRef.current
    const step = s.activeScript?.steps?.[s.currentStepIndex]
    // Close any flow this step opened before leaving.
    if (step?.closeOnLeave?.action) emitTourEvent(TOUR_EVENT_TYPES.TOUR_ACTION, { action: step.closeOnLeave.action })
    // On a wait-step, "Skip step" follows the step's cancel route (e.g. skip the
    // wow moment when no appliance was added) instead of blindly advancing +1.
    let target = s.currentStepIndex + 1
    if (step?.cancelOn?.to) {
      target = step.cancelOn.to === 'next'
        ? s.currentStepIndex + 1
        : s.activeScript.steps.findIndex(x => x.id === step.cancelOn.to)
    }
    await goToIndex(target, { completedStepId: step?.id, eventType: 'step_skipped' })
  }, [goToIndex])

  const skipTour = useCallback(async (reason = 'user_skipped') => {
    const s = stateRef.current
    const step = s.activeScript?.steps?.[s.currentStepIndex]
    if (step?.closeOnLeave?.action) emitTourEvent(TOUR_EVENT_TYPES.TOUR_ACTION, { action: step.closeOnLeave.action })
    const uid = userIdRef.current
    await persistProgress(uid, s.activeTourId, s.homeContext.homeId, {
      dismissed_at: nowIso(),
      abandoned_mid_step: s.currentStepIndex > 0,
    })
    await logEvent(uid, s.activeTourId, s.homeContext.homeId, 'tour_dismissed', {
      stepId: step?.id,
      eventData: { reason },
    })
    dispatch({ type: 'END' })
  }, [])

  const remindLater = useCallback(async () => {
    const s = stateRef.current
    const step = s.activeScript?.steps?.[s.currentStepIndex]
    if (step?.closeOnLeave?.action) emitTourEvent(TOUR_EVENT_TYPES.TOUR_ACTION, { action: step.closeOnLeave.action })
    const uid = userIdRef.current
    await persistProgress(uid, s.activeTourId, s.homeContext.homeId, {
      remind_later_at: nowIso(),
      abandoned_mid_step: s.currentStepIndex > 0,
    })
    await logEvent(uid, s.activeTourId, s.homeContext.homeId, 'tour_remind_later', {
      stepId: step?.id,
    })
    dispatch({ type: 'END' })
  }, [])

  const completeTour = useCallback(async () => { await completeTourInternal() }, [completeTourInternal])

  const enterOptionalBranch = useCallback(async (branchId) => {
    const s = stateRef.current
    dispatch({ type: 'ENTER_BRANCH', branchId })
    await logEvent(userIdRef.current, s.activeTourId, s.homeContext.homeId, 'optional_step_taken', {
      stepId: s.activeScript?.steps?.[s.currentStepIndex]?.id,
      eventData: { branchId },
    })
  }, [])

  const exitOptionalBranch = useCallback(async () => {
    const s = stateRef.current
    dispatch({ type: 'EXIT_BRANCH' })
    await logEvent(userIdRef.current, s.activeTourId, s.homeContext.homeId, 'optional_step_declined', {
      stepId: s.activeScript?.steps?.[s.currentStepIndex]?.id,
    })
  }, [])

  // Ask the host surface (Dashboard) to do something — never reach into its
  // state directly. Dashboard subscribes to TOUR_ACTION.
  const dispatchAction = useCallback((actionName, payload = {}) => {
    emitTourEvent(TOUR_EVENT_TYPES.TOUR_ACTION, { action: actionName, payload })
  }, [])

  // Generic analytics passthrough for steps that log their own events
  // (e.g. wow_moment_hit / wow_moment_miss).
  const track = useCallback((eventType, extra = {}) => {
    const s = stateRef.current
    logEvent(userIdRef.current, s.activeTourId ?? extra.tourId ?? null, s.homeContext.homeId, eventType, extra)
  }, [])

  // Wait-for-signal: a step with `waitsForSignal` advances/cancels on app-bus
  // flow-completion events instead of a tour "Continue". Subscribe for the life
  // of the step, unsubscribe on step change. See welcomeScript + src/lib/appEvents.
  const currentStepId = currentStep?.id
  useEffect(() => {
    const s0 = stateRef.current
    const idx = s0.currentStepIndex
    const step = s0.activeScript?.steps?.[idx]
    if (!step?.waitsForSignal?.length) return
    const stepsArr = s0.activeScript.steps
    const resolveTo = (to) => (to === 'next' ? idx + 1 : stepsArr.findIndex(x => x.id === to))
    let fired = false
    const offs = step.waitsForSignal.map(evt => onTourEvent(evt, () => {
      if (fired) return
      const isAdvance = step.advanceOn?.event === evt
      const isCancel = step.cancelOn?.event === evt
      if (!isAdvance && !isCancel) return
      fired = true
      const target = resolveTo(isAdvance ? step.advanceOn.to : step.cancelOn.to)
      // Leaving a step that opened a modal must close it — on signal-driven
      // advance/cancel too, not only user-driven exits. (No-op if already closed,
      // e.g. AddAppliance's handleSaved already closed it.)
      if (step.closeOnLeave?.action) {
        emitTourEvent(TOUR_EVENT_TYPES.TOUR_ACTION, { action: step.closeOnLeave.action })
      }
      goToIndex(target, { completedStepId: step.id, eventType: isAdvance ? 'step_completed' : 'step_skipped' })
    }))
    return () => { offs.forEach(off => off()) }
  }, [currentStepId, goToIndex])

  const value = useMemo(() => ({
    state: { ...state, currentStep, totalSteps: steps.length },
    actions: {
      startTour,
      nextStep,
      previousStep,
      skipStep,
      skipTour,
      remindLater,
      completeTour,
      enterOptionalBranch,
      exitOptionalBranch,
      dispatchAction,
      track,
    },
  }), [state, currentStep, steps.length, startTour, nextStep, previousStep, skipStep,
       skipTour, remindLater, completeTour, enterOptionalBranch, exitOptionalBranch,
       dispatchAction, track])

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}
